import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Card, CardMedia, CardContent, CardActions,
  CardActionArea, Button, Select, MenuItem, FormControl, InputLabel, Slider,
  Drawer, IconButton, CircularProgress, useTheme, useMediaQuery, Paper, Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
// import Navbar from '../components/Navbar';
// import Footer from '../components/Footer';

/* ─────────────────────────────────────────────────────────────────────────── */
export default function ShopPage() {
  const theme      = useTheme();
  const mdUp       = useMediaQuery(theme.breakpoints.up('md'));
  const smDown     = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate   = useNavigate();

  const [products, setProducts]         = useState([]);
  const [categories, setCategories]     = useState([]);
  const [selectedCategory, setCategory] = useState('');
  const [sortBy, setSortBy]             = useState('');
  const [priceRange, setPriceRange]     = useState([0, 500]);
  const [maxPrice, setMaxPrice]         = useState(500);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [drawerOpen, setDrawerOpen]     = useState(false);

  useEffect(() => {
    fetch('https://lemda.co.uk/web_api/products.php')
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => {
        setProducts(d);
        setCategories([...new Set(d.map(p => p.category))]);
        const mx = Math.ceil(Math.max(...d.map(p => p.price)));
        setMaxPrice(mx); setPriceRange([0, mx]);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = products
    .filter(p => !selectedCategory || p.category === selectedCategory)
    .filter(p => p.price >= priceRange[0] && p.price <= priceRange[1])
    .sort((a, b) => {
      if (sortBy === 'priceAsc')  return a.price - b.price;
      if (sortBy === 'priceDesc') return b.price - a.price;
      if (sortBy === 'name')      return a.name.localeCompare(b.name);
      return 0;
    });

  /* ── Sidebar component (unchanged) ─────────────────────────────────────── */
  const Sidebar = () => (
    <Box sx={{ width: 260, p: 3 }}>
      <Typography variant="h6" gutterBottom>Filters</Typography>
      {/* Category */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Category</InputLabel>
        <Select value={selectedCategory} label="Category"
                onChange={e => setCategory(e.target.value)}>
          <MenuItem value=""><em>All</em></MenuItem>
          {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      </FormControl>
      {/* Price */}
      <Typography gutterBottom>Price Range</Typography>
      <Slider value={priceRange} max={maxPrice}
              onChange={(_, v) => setPriceRange(v)}
              valueLabelDisplay="auto" sx={{ mb: 3 }}/>
      {/* Sort */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Sort By</InputLabel>
        <Select value={sortBy} label="Sort By" onChange={e => setSortBy(e.target.value)}>
          <MenuItem value=""><em>None</em></MenuItem>
          <MenuItem value="priceAsc">Price: Low → High</MenuItem>
          <MenuItem value="priceDesc">Price: High → Low</MenuItem>
          <MenuItem value="name">Name A → Z</MenuItem>
        </Select>
      </FormControl>
      <Button variant="outlined" fullWidth
              onClick={() => { setCategory(''); setSortBy(''); setPriceRange([0, maxPrice]); }}>
        Clear All
      </Button>
    </Box>
  );

  if (loading) return <PageLoad />;
  if (error)   return <PageError msg={error} />;

  return (
    <>
      {/* <Navbar /> */}
      {/* Mobile filter toggle */}
      {smDown && (
        <IconButton onClick={() => setDrawerOpen(true)}
                    sx={{ position: 'fixed', top: 80, right: 16, zIndex: 1300 }}>
          <MenuIcon fontSize="large" />
        </IconButton>
      )}

      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {/* Sidebar */}
        {mdUp
          ? <Paper elevation={0} sx={{
              flex: '0 0 260px', borderRight: 1, borderColor: 'divider',
              bgcolor: theme.palette.mode === 'light'
                ? 'rgba(255,255,255,0.6)' : 'rgba(24,24,24,0.55)',
              backdropFilter: 'blur(8px)'
            }}>
              <Sidebar />
            </Paper>
          : <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}><Sidebar /></Drawer>
        }

        {/* Main grid */}
        <Container maxWidth="xl" sx={{ py: 5, flex: 1 }}>
          <Typography variant="h4" gutterBottom>Marketplace</Typography>

          {filtered.length === 0
            ? <Empty />
            : (
              <Box sx={{
                display: 'grid', gap: 3,
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(4, 1fr)',
                  lg: 'repeat(5, 1fr)',
                }
              }}>
                {filtered.map(p => (
                  <ProductCard key={p.id} data={p}
                               onClick={() => navigate(`/products/${p.id}`)} />
                ))}
              </Box>
            )}
        </Container>
      </Box>
      {/* <Footer /> */}
    </>
  );
}

/* ───────────────────────────── Card ─────────────────────────────────────── */
function ProductCard({ data, onClick }) {
  const theme = useTheme();
  const madeText = data.made ?? '';                     // safe fallback
  const isLemda  = madeText.toLowerCase().includes('lemda');

  return (
    <Card
      component={motion.div}
      elevation={1}
      whileHover={{ y: -4, boxShadow: theme.shadows[6] }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      sx={{
        display: 'flex', flexDirection: 'column',
        borderRadius: 3, overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.mode === 'light'
          ? 'rgba(255,255,255,0.55)' : 'rgba(30,30,30,0.55)',
        backdropFilter: 'blur(6px)'
      }}
    >
        
      <CardActionArea onClick={onClick} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Image */}
        <Box sx={{ width: '100%', aspectRatio: '4/3', bgcolor: '#f5f5f5' }}>
        {/* render badge ONLY if field exists */}
          {madeText && (
            <Chip
              label={madeText}
              size="small"
              color={isLemda ? 'primary' : 'default'}
              sx={{ position: 'absolute', top: 6, right: 6 }}
            />
          )}
          <CardMedia component="img" image={data.image_url} alt={data.name}
                     sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1.5 }} />
        </Box>

        <CardContent sx={{ flexGrow: 1, width: '100%', position: 'relative' }}>
          

          <Typography variant="subtitle2" fontWeight={700} noWrap>{data.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {data.description}
          </Typography>
        </CardContent>
      </CardActionArea>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 1 }}>
        <Typography fontWeight={700} variant="subtitle2">${data.price.toFixed(2)}</Typography>
        <Button size="small" onClick={onClick}>Details</Button>
      </CardActions>
    </Card>
  );
}

/* ───────────────────────── Misc states ─────────────────────────────────── */
const PageLoad = () => (
  <>
    {/* <Navbar /> */}
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>
    {/* <Footer /> */}
  </>
);
const PageError = ({ msg }) => (
  <>
    {/* <Navbar /> */}
    <Container sx={{ py: 10 }}><Typography color="error" variant="h6">Error: {msg}</Typography></Container>
    {/* <Footer /> */}
  </>
);
const Empty = () => (
  <Box sx={{ textAlign: 'center', py: 10 }}>
    <Typography variant="h6" gutterBottom>No items match your filters.</Typography>
    <Typography color="text.secondary">Try changing category or price range.</Typography>
  </Box>
);
